// Source: D:/AI/site-queensbutler/public/design-patterns.html — B-2a / B-2b / B-3a
import { useState as useSubState } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useCompactHeader } from './useCompactHeader'
import { shopLive } from '@/lib/shopLive'
import { Link, useLocation } from 'react-router-dom'
import { type ServiceKey } from '@/data/nav'
import { navFor } from '@/data/nav.en'
import routesData from '@/data/routes.json'
import { getLang, stripEn, withLang } from '@/lib/lang'
import { pageLabel } from '@/lib/pageContent'
import { LangSwitch } from './GlobalHeader'
import type { FixedRouteData } from '@/data/types'

interface ServiceHeaderProps { service: ServiceKey }
const crest = '/img/catalog/mark_g04_gold_512.png' // Yuichi 2026-09-17「G04筆の環 … いったん、全ページのロゴをこれに差し替えろ」
const tone: Record<ServiceKey, string> = { salon: 'salon', studio: 'studio', apprentice: 'footman', shop: 'shop' }
const serviceEnglish: Record<ServiceKey, string[]> = {
  salon: ['About', 'Experience', 'Buddy', 'Price', 'FAQ', 'Contact'],
  studio: ['About', 'Plans', 'Scenes', 'Price', 'FAQ', 'Contact'],
  apprentice: ['About', 'Program', 'Wardrobe', 'Price', 'FAQ', 'Contact'],
  shop: ['About', 'Items', 'Collection', 'Guide', 'FAQ', 'Contact'],
}

interface ServiceRoute {
  url: string
  name: string
  parentUrl: string | null
  en?: string
}

const serviceRoutes = routesData.routes as ServiceRoute[]

function routeEnglish(route: ServiceRoute) {
  if (route.en) return route.en
  const slug = route.url.split('/').filter(Boolean).at(-1) ?? ''
  return slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function childRoutes(href: string) {
  return serviceRoutes.filter((route) => route.parentUrl === stripEn(href))
}


export function ServiceHeader({ service }: ServiceHeaderProps) {
  const { pathname } = useLocation()
  // 言語は pathname から判定。ナビ・文言は nav.en.ts の navFor で切り替え、構造・CSS は共通
  const lang = getLang(pathname)
  const { serviceNav, ui: t } = navFor(lang)
  const config = serviceNav[service]
  const otherServices = Object.values(serviceNav).filter((item) => item.key !== service)
  // ドロップダウン子項目: JA は routes.json の name、EN は EN コンテンツの meta.navLabel ?? h1(仕様 §2-7)
  const childLabel = (child: ServiceRoute) => (lang === 'en' ? pageLabel(child as unknown as FixedRouteData, 'en') : child.name)
  const compact = useCompactHeader()
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [closedSub, setClosedSub] = useSubState<string | null>(null) // Yuichi 2026-09-16「サブメニューを選択した際に、サブメニューを一度閉じて画面を切り替わった感覚を」
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLElement>(null)
  useEffect(() => { try { const v = sessionStorage.getItem('qb_sub_closed'); if (v) { setClosedSub(v); sessionStorage.removeItem('qb_sub_closed') } } catch {} }, [pathname]) // Yuichi 2026-09-17「サブメニューのメニューを選択したらサブメニューエリアのウインドウを閉じろ…まだ治っていない」: ページ遷移後もマウスが離れるまで閉じたままにする
  useEffect(() => setOpen(false), [pathname])
  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    menuRef.current?.querySelector<HTMLAnchorElement>('a')?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        menuButtonRef.current?.focus()
        return
      }
      if (event.key !== 'Tab' || !menuRef.current) return
      const focusable = [...menuRef.current.querySelectorAll<HTMLElement>('a,button')]
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable.at(-1)
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => { document.removeEventListener('keydown', onKeyDown); document.body.style.overflow = previousOverflow }
  }, [open])

  return (
    <header className={`site-header${compact ? ' is-compact' : ''}`}>
      <div className="desktop-header">
        {!compact && <div className="sh-strip"><div className="shell"><LangSwitch lang={lang} pathname={pathname} label={t.langLabel} />
          <Link to={t.home}>{t.backToTop}</Link>
          {otherServices.map((item) => <Link key={item.key} to={withLang(item.prefix, lang)}>{item.label}</Link>)}
        </div></div>}
        <div className={`sh ${tone[service]}${compact ? ' compact' : ''}`}><div className="shell">
          <Link className="brand" to={withLang(config.prefix, lang)} aria-label={t.serviceTop(config.label)}>
            <img className="mark-u02" src="/img/catalog/mark_g04_gold_512.png" alt={lang === 'en' ? "Queen's Butler mark" : "Queen's Butler のマーク（G04 筆の環）"} width="512" height="476" />
            <span className="svc">{config.lockup}</span><span className="qb">{config.subLockup}</span>{/* Yuichi 2026-09-17「クイーズバトラーの下にある執事サロンという文字を消せ」: 日本語ラベルは aria-label に残し表示しない */}
          </Link>
          <nav aria-label={t.navService(config.label)}>
            {config.links.map((item, index) => {
              const isContact = index === config.links.length - 1
              const children = isContact ? [] : childRoutes(item.href)
              if (isContact || children.length === 0) {
                return <Link className={isContact ? 'btn sm ghost-w' : undefined} key={item.href} to={item.href}>{item.label}</Link>
              }
              return (
                <div className={`nav-group${closedSub === item.href ? ' closed' : ''}`} key={item.href} onMouseLeave={() => setClosedSub(null)}>
                  <Link className="has" to={item.href}>{item.label}</Link>
                  <div className="gh-sub nav-dropdown"><div className="shell" data-cols={Math.min(4, children.length + 1)}>
                    <Link className="sub-parent" to={item.href} onClick={() => { setClosedSub(item.href); try { sessionStorage.setItem('qb_sub_closed', item.href) } catch {} }}><small>{serviceEnglish[service][index]}</small>{item.label}{t.listSuffix}</Link>
                    {children.map((child) => <Link key={child.url} to={withLang(child.url, lang)} onClick={() => { setClosedSub(item.href); try { sessionStorage.setItem('qb_sub_closed', item.href) } catch {} }}><small>{routeEnglish(child)}</small>{childLabel(child)}</Link>)}
                  </div></div>
                </div>
              )
            })}
            {config.cart && shopLive && <Link className="btn sm ghost-w" to={config.cart.href}>{t.cart}</Link>}
          </nav>
        </div></div>
      </div>

      <div className="mobile-header">
        <div className={`sph ${tone[service]}`}>
          <Link className="lk" to={withLang(config.prefix, lang)}><img className="coronet" src="/img/catalog/mark_g04_gold_512.png" alt="" width="512" height="476" /><span className="tx"><span className="qb-up">Queen's Butler</span><span className="svc">{config.lockup}</span></span></Link>
          <button ref={menuButtonRef} className="ham" type="button" aria-expanded={open} aria-controls="service-mobile-menu" aria-label={open ? t.closeNav : t.openNav} onClick={() => setOpen((value) => !value)}>
            {open ? <span aria-hidden="true">×</span> : <span aria-hidden="true"><i /><i /><i /></span>}
          </button>
        </div>
        {open && <nav ref={menuRef} className="spm" id="service-mobile-menu" aria-label={t.navServiceMobile(config.label)}>
          {config.links.slice(0, -1).map((item, index) => {
            const children = childRoutes(item.href)
            const exp = expanded === item.href
            return <div className={`spm-group${exp ? ' open' : ''}`} key={item.href}>
              <div className="spm-row">
                <Link className="spm-parent" to={item.href}>{item.label}<small>{serviceEnglish[service][index]}</small></Link>
                {children.length > 0 && <button type="button" className="spm-tg" aria-expanded={exp} aria-controls={`sm-${index}`} aria-label={exp ? t.subClose(item.label) : t.subOpen(item.label)} onClick={() => setExpanded(exp ? null : item.href)}><i aria-hidden="true" /></button>}
              </div>
              {children.length > 0 && exp && <div className="spm-children" id={`sm-${index}`}>
                <Link className="sub-parent" to={item.href} onClick={() => { setClosedSub(item.href); try { sessionStorage.setItem('qb_sub_closed', item.href) } catch {} }}><small>{serviceEnglish[service][index]}</small>{item.label}{t.listSuffix}</Link>
                {children.map((child) => <Link key={child.url} to={withLang(child.url, lang)} onClick={() => { setClosedSub(item.href); try { sessionStorage.setItem('qb_sub_closed', item.href) } catch {} }}><small>{routeEnglish(child)}</small>{childLabel(child)}</Link>)}
              </div>}
            </div>
          })}
          <div className="oth"><LangSwitch lang={lang} pathname={pathname} label={t.langLabel} /><Link to={t.home}>{t.backToTop}</Link>{otherServices.map((item) => <Link key={item.key} to={withLang(item.prefix, lang)}>{item.label}</Link>)}</div>
          <Link className="btn rose" to={config.links.at(-1)?.href ?? withLang('/contact/', lang)}>{config.links.at(-1)?.label ?? t.contact}</Link>
          {config.cart && shopLive && <Link className="btn ghost-w" to={config.cart.href}>{t.cart}</Link>}
        </nav>}
      </div>
    </header>
  )
}
